import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  FormErrorMessage,
  FormHelperText,
} from '@chakra-ui/react';
import { useEmail } from '../../hooks/useEmail';

export function TemplateEmailForm() {
  const { sendTemplateEmail, loading, error, clearError } = useEmail();
  const [to, setTo] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [data, setData] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!to.trim()) {
      errors.to = 'Recipient email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) {
      errors.to = 'Invalid email address';
    }

    if (!templateId.trim()) {
      errors.templateId = 'Template ID is required';
    }

    if (data.trim()) {
      try {
        JSON.parse(data);
      } catch {
        errors.data = 'Invalid JSON format';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const parseEmails = (value: string): string[] => {
    return value
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    clearError();

    if (!validateForm()) {
      return;
    }

    const emailData = {
      to: to.trim(),
      templateId: templateId.trim(),
      ...(data.trim() && { data: JSON.parse(data) }),
      ...(cc.trim() && { cc: parseEmails(cc) }),
      ...(bcc.trim() && { bcc: parseEmails(bcc) }),
    };

    const result = await sendTemplateEmail(emailData);

    if (result?.success) {
      setSuccess(`Template email sent successfully! ID: ${result.id}`);
      setTo('');
      setTemplateId('');
      setData('');
      setCc('');
      setBcc('');
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        {success && (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Success!</AlertTitle>
              <AlertDescription>{success}</AlertDescription>
            </Box>
            <CloseButton
              position="relative"
              right={-1}
              top={-1}
              onClick={() => setSuccess(null)}
            />
          </Alert>
        )}

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
            <CloseButton
              position="relative"
              right={-1}
              top={-1}
              onClick={clearError}
            />
          </Alert>
        )}

        <FormControl isRequired isInvalid={!!validationErrors.to}>
          <FormLabel>To</FormLabel>
          <Input
            type="email"
            placeholder="recipient@example.com"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <FormErrorMessage>{validationErrors.to}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isInvalid={!!validationErrors.templateId}>
          <FormLabel>Template ID</FormLabel>
          <Input
            placeholder="re_template_xxxxxxxxxx"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          />
          <FormHelperText>
            The Resend template ID from your dashboard
          </FormHelperText>
          <FormErrorMessage>{validationErrors.templateId}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!validationErrors.data}>
          <FormLabel>Template Data (JSON)</FormLabel>
          <Textarea
            placeholder='{"name": "John", "company": "Acme Inc"}'
            value={data}
            onChange={(e) => setData(e.target.value)}
            minH="120px"
            fontFamily="mono"
          />
          <FormHelperText>
            Variables to pass to the template (optional)
          </FormHelperText>
          <FormErrorMessage>{validationErrors.data}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel>CC (comma-separated)</FormLabel>
          <Input
            placeholder="cc1@example.com, cc2@example.com"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>BCC (comma-separated)</FormLabel>
          <Input
            placeholder="bcc1@example.com, bcc2@example.com"
            value={bcc}
            onChange={(e) => setBcc(e.target.value)}
          />
        </FormControl>

        <Button
          type="submit"
          colorScheme="blue"
          isLoading={loading}
          loadingText="Sending..."
          size="lg"
          mt={4}
        >
          Send Template Email
        </Button>
      </VStack>
    </Box>
  );
}
